import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export default function LoadingIcon() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // 使用主题颜色 - 浅色模式使用更深的颜色
  const primaryColor = isDark ? theme.palette.primary.main : theme.palette.primary.dark;
  const secondaryColor = isDark ? theme.palette.secondary.main : theme.palette.secondary.dark;
  const primaryLight = theme.palette.primary.light;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '--size': '80px',
        '--speed': '1.25s',
        '--accent-1': primaryColor,
        '--accent-2': secondaryColor,
        '--accent-light': primaryLight,
      }}
    >
      <Box
        className="nebula"
        aria-label="Loading"
        sx={{
          width: 'var(--size)',
          height: 'var(--size)',
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
        }}
      >
        {/* 发光旋转环 */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: `conic-gradient(from 120deg, var(--accent-1), var(--accent-2), var(--accent-light), var(--accent-1))`,
            WebkitMask: 'radial-gradient(farthest-side, transparent 58%, black 59%)',
            mask: 'radial-gradient(farthest-side, transparent 58%, black 59%)',
            filter: isDark ? 'blur(8px) saturate(140%)' : 'blur(6px) saturate(180%)',
            animation: 'rotate-nebula calc(var(--speed) * 1.8) linear infinite',
            transformOrigin: 'center',
            position: 'absolute',
            inset: 0,
            '@keyframes rotate-nebula': {
              from: { transform: 'rotate(0deg) scale(1)' },
              '50%': { transform: 'rotate(180deg) scale(1.02)' },
              to: { transform: 'rotate(360deg) scale(1)' },
            },
          }}
        />
        {/* 中心小球 - 只在深色模式显示 */}
        {isDark && (
          <Box
            sx={{
              width: '36%',
              height: '36%',
              borderRadius: '50%',
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.12), transparent)',
              boxShadow: `0 6px 24px rgba(0, 0, 0, 0.6), 0 0 20px ${primaryColor}20, 0 0 40px ${secondaryColor}10`,
              position: 'relative',
              zIndex: 2,
            }}
          />
        )}
        {/* 环绕粒子 */}
        {[1, 2, 3, 4, 5].map((i) => (
          <Box
            key={i}
            sx={{
              '--s': '10px',
              position: 'absolute',
              width: 'var(--s)',
              height: 'var(--s)',
              borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #fff, rgba(255, 255, 255, 0.6) 10%, transparent 40%)',
              filter: 'blur(1px)',
              opacity: 0.95,
              transformOrigin: 'center',
              animation: 'orbit calc(var(--speed) * 2) linear infinite',
              ...(i === 1 && { top: '6%', left: '50%', animationDelay: '-0.1s' }),
              ...(i === 2 && { top: '20%', left: '85%', animationDelay: '-0.25s', transform: 'translate(-50%, -50%) scale(0.9)' }),
              ...(i === 3 && { top: '85%', left: '75%', animationDelay: '-0.45s' }),
              ...(i === 4 && { top: '78%', left: '18%', animationDelay: '-0.65s' }),
              ...(i === 5 && { top: '30%', left: '8%', animationDelay: '-0.9s' }),
              '@keyframes orbit': {
                from: { transform: 'rotate(0deg) translateY(calc(var(--size) * -0.28)) rotate(0deg)' },
                to: { transform: 'rotate(360deg) translateY(calc(var(--size) * -0.28)) rotate(-360deg)' },
              },
            }}
          />
        ))}
      </Box>
      <Typography
        variant="body2"
        sx={{
          mt: 2,
          color: primaryColor,
          fontWeight: 600,
          letterSpacing: '0.5px',
          fontSize: '0.875rem',
        }}
      >
        Loading...
      </Typography>
    </Box>
  );
}
